import type { CatalogueDesignKey } from '@eshop/designs';
import type { ItemCardSize } from '@eshop/templates/system/templateTypes';

export type CatalogueGridBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export type CatalogueGridColumnMatrix = Record<CatalogueGridBreakpoint, number>;

const SIZE_COLUMN_MATRIX: Record<ItemCardSize, CatalogueGridColumnMatrix> = {
  small: {
    xs: 2,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 6,
    xxl: 6,
  },
  normal: {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 3,
    xl: 4,
    xxl: 4,
  },
  large: {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 3,
    xl: 3,
    xxl: 3,
  },
};

export const DEFAULT_ITEM_CARD_SIZE: ItemCardSize = 'normal';

const copyMatrix = (matrix: CatalogueGridColumnMatrix): CatalogueGridColumnMatrix => ({
  xs: matrix.xs,
  sm: matrix.sm,
  md: matrix.md,
  lg: matrix.lg,
  xl: matrix.xl,
  xxl: matrix.xxl,
});

export const catalogueGridColumns = (
  design?: CatalogueDesignKey,
  itemCardSize: ItemCardSize = DEFAULT_ITEM_CARD_SIZE,
): CatalogueGridColumnMatrix => {
  const base = SIZE_COLUMN_MATRIX[itemCardSize] ?? SIZE_COLUMN_MATRIX[DEFAULT_ITEM_CARD_SIZE];

  if (design === 'minimalista') {
    return copyMatrix(base);
  }

  return copyMatrix(base);
};

export type CatalogueTokenScale = {
  space: {
    xxs: string;
    xs: string;
    sm: string;
    md: string;
    lg: string;
  };
  typography: {
    eyebrow: { fontSize: string; lineHeight: string };
    body: { fontSize: string; lineHeight: string };
    heading: { fontSize: string; lineHeight: string };
    highlight: { fontSize: string; lineHeight: string };
  };
  colors: {
    textStrong: string;
    textMuted: string;
    accent: string;
    surface: string;
    surfaceMuted: string;
    borderSoft: string;
  };
  radii: {
    panel: string;
    card: string;
    cardMedia: string;
    cta: string;
  };
  shadow: {
    card: string;
    panel: string;
  };
  layout: {
    heroPaddingInline: string;
    heroPaddingBlock: string;
    panelPaddingInline: string;
    panelPaddingBlock: string;
    gridGapX: string;
    gridGapY: string;
  };
};

export const minimalistaCatalogueTokens: CatalogueTokenScale = {
  space: {
    xxs: 'var(--catalogue-space-1)',
    xs: 'var(--catalogue-space-2)',
    sm: 'var(--catalogue-space-3)',
    md: 'var(--catalogue-space-4)',
    lg: 'var(--catalogue-space-5)',
  },
  typography: {
    eyebrow: {
      fontSize: 'var(--catalogue-font-size-100)',
      lineHeight: 'var(--catalogue-line-height-tight)',
    },
    body: {
      fontSize: 'var(--catalogue-font-size-200)',
      lineHeight: 'var(--catalogue-line-height-relaxed)',
    },
    heading: {
      fontSize: 'var(--catalogue-font-size-400)',
      lineHeight: 'var(--catalogue-line-height-tight)',
    },
    highlight: {
      fontSize: 'var(--catalogue-font-size-300)',
      lineHeight: 'var(--catalogue-line-height-tight)',
    },
  },
  colors: {
    textStrong: 'var(--catalogue-text-strong)',
    textMuted: 'var(--catalogue-text-muted)',
    accent: 'var(--catalogue-accent)',
    surface: 'var(--catalogue-card-bg)',
    surfaceMuted: 'var(--catalogue-panel-bg)',
    borderSoft: 'var(--catalogue-border-soft)',
  },
  radii: {
    panel: 'var(--catalogue-panel-radius)',
    card: 'var(--catalogue-card-radius)',
    cardMedia: 'var(--catalogue-card-image-radius)',
    cta: 'var(--catalogue-cta-radius)',
  },
  shadow: {
    card: 'var(--catalogue-card-shadow)',
    panel: 'var(--catalogue-panel-shadow)',
  },
  layout: {
    heroPaddingInline: 'var(--catalogue-hero-padding-inline)',
    heroPaddingBlock: 'var(--catalogue-hero-padding-block)',
    panelPaddingInline: 'var(--catalogue-panel-padding-inline)',
    panelPaddingBlock: 'var(--catalogue-panel-padding-block)',
    gridGapX: 'var(--catalogue-grid-gap-x)',
    gridGapY: 'var(--catalogue-grid-gap-y)',
  },
};

export const DEFAULT_CATALOGUE_GRID_GUTTER: [number, number] = [16, 24];
export const MINIMALISTA_CATALOGUE_GRID_GUTTER: [number, number] = [20, 32];
export const LIST_CATALOGUE_GRID_GUTTER: [number, number] = [0, 24];

export const getCatalogueGridGutter = (
  design?: CatalogueDesignKey,
  viewMode: 'grid' | 'list' = 'grid',
): [number, number] => {
  if (viewMode === 'list') {
    return [...LIST_CATALOGUE_GRID_GUTTER];
  }

  if (design === 'minimalista') {
    return [...MINIMALISTA_CATALOGUE_GRID_GUTTER];
  }

  return [...DEFAULT_CATALOGUE_GRID_GUTTER];
};
